import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { 
  useReactTable, 
  getCoreRowModel, 
  getFilteredRowModel, 
  getPaginationRowModel,
  getSortedRowModel,
  flexRender 
} from '@tanstack/react-table';
import { getSuppliers, deleteSupplierNotice } from '../../../models/noticesModel';

function SupplierNotices() {
  const navigate = useNavigate();
  const [notices, setNotices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [globalFilter, setGlobalFilter] = useState('');
  const [sorting, setSorting] = useState([]);

  useEffect(() => {
    fetchNotices();
  }, []);

  const fetchNotices = async () => {
    try {
      setLoading(true);
      const response = await getSuppliers();
      console.log('Supplier Notices API response:', response); // Debug log
      if (response.status === 1) {
        // Use the supplier notices data - the 320 records that were working
        setNotices(response.data || []);
      }
    } catch (error) {
      console.error('Error fetching supplier notices:', error);
      toast.error('Failed to load supplier notices');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateNotice = () => {
    // Navigate to create supplier notice page
    navigate('/dashboard/notices/supplier/create');
  };

  const handleViewNotice = (noticeId) => {
    // Navigate to view supplier notice page
    navigate(`/dashboard/notices/supplier/view/${noticeId}`);
  };

  const handleEditNotice = (noticeId) => {
    // Navigate to edit supplier notice page
    navigate(`/dashboard/notices/supplier/edit/${noticeId}`);
  };

  const handleDeleteNotice = async (notice) => {
    if (window.confirm(`Are you sure you want to delete "${notice.notice_title}"?`)) {
      try {
        const response = await deleteSupplierNotice(notice.notice_id, notice.notice_title);
        if (response.status === 1) {
          toast.success('Notice deleted successfully!');
          fetchNotices(); // Refresh the list
        } else {
          throw new Error(response.message || 'Failed to delete notice');
        }
      } catch (error) {
        console.error('Error deleting notice:', error);
        toast.error('Failed to delete notice');
      }
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp * 1000);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const columns = [
    {
      accessorKey: 'notice_time',
      header: 'Created',
      cell: ({ getValue }) => formatDate(getValue()),
      size: 120,
    },
    {
      accessorKey: 'notice_title',
      header: 'Title',
      cell: ({ getValue }) => (
        <div className="max-w-xs truncate" title={getValue()}>
          {getValue()}
        </div>
      ),
    },
    {
      accessorKey: 'notice_status',
      header: 'Status',
      cell: ({ getValue }) => {
        const status = getValue();
        return (
          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
            status == 1 ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
          }`}>
            {status == 1 ? 'Sent' : 'Draft'}
          </span>
        );
      },
      size: 100,
    },
    {
      id: 'actions',
      header: () => <div className="text-center">Actions</div>,
      cell: ({ row }) => (
        <div className="flex items-center justify-center space-x-1">
          <button
            onClick={() => handleViewNotice(row.original.notice_id)}
            className="px-2 py-1 bg-blue-500 hover:bg-blue-600 text-white rounded text-xs"
            title="View Notice"
          >
            View
          </button>
          <button
            onClick={() => handleEditNotice(row.original.notice_id)}
            className="px-2 py-1 bg-yellow-500 hover:bg-yellow-600 text-white rounded text-xs"
            title="Edit Notice"
          >
            Edit
          </button>
          <button
            onClick={() => handleDeleteNotice(row.original)}
            className="px-2 py-1 bg-red-500 hover:bg-red-600 text-white rounded text-xs"
            title="Delete Notice"
          >
            Delete
          </button>
        </div>
      ),
      enableSorting: false,
      size: 150,
    },
  ];

  const table = useReactTable({
    data: notices,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    state: {
      globalFilter,
      sorting,
    },
    onGlobalFilterChange: setGlobalFilter,
    onSortingChange: setSorting,
    initialState: {
      pagination: {
        pageSize: 20,
      },
    },
  });

  if (loading) {
    return (
      <div className="p-6 md:p-10 flex justify-center items-center h-[calc(100vh-150px)]">
        <div 
          className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4" 
          style={{ borderColor: 'var(--color-secondary-default)' }}
        ></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      {/* Professional Header Card */}
      <div className="container mx-auto px-4 py-4">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-2">
          <div className="flex items-start justify-between">
            {/* Left: Icon, Title, Description, Results */}
            <div className="flex items-start">
              <div className="flex-shrink-0 rounded-lg p-2.5 mr-3 mt-0.5" style={{ backgroundColor: 'rgba(0, 135, 212, 0.15)' }}>
                <svg className="h-5 w-5" fill="none" stroke="#0087D4" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-5 5-5-5h5z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7H4l5-5 5 5H9z" />
                </svg>
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-800">Supplier Notices</h1>
                <p className="text-xs text-gray-500 mb-1">Communication & Announcements</p>
                <div className="flex items-center mt-1">
                  <span className="text-xs font-medium" style={{ color: '#0087D4' }}>
                    {table.getFilteredRowModel().rows.length} notices found
                    {globalFilter && (
                      <span className="ml-1 text-blue-600">(filtered)</span>
                    )}
                  </span>
                </div>
              </div>
            </div>
            
            {/* Right: All Controls and Search */}
            <div className="flex flex-col gap-2 items-end min-w-0">
              {/* Row 1: Action Buttons */}
              <div className="flex items-center gap-2">
                {/* Refresh Button */}
                <button
                  onClick={fetchNotices}
                  disabled={loading}
                  className="flex-shrink-0 inline-flex items-center px-3 py-1.5 border border-gray-300 rounded-md shadow-sm text-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:opacity-50"
                  title="Refresh"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} fill="none" viewBox="0 0 24 24" stroke={loading ? '#0096DE' : 'currentColor'} style={{ color: loading ? '#0096DE' : 'currentColor' }}>
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                </button>

                {/* Page Size Filter */}
                <div className="flex-shrink-0">
                  <select
                    value={table.getState().pagination.pageSize}
                    onChange={e => {
                      table.setPageSize(Number(e.target.value));
                    }}
                    className="text-sm px-2 py-1.5 border border-gray-300 bg-white rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  >
                    {[10, 20, 50, 100].map(size => (
                      <option key={size} value={size}>{size} per page</option>
                    ))}
                  </select>
                </div>

                {/* Create Button */}
                <button
                  onClick={handleCreateNotice}
                  className="inline-flex items-center px-3 py-1.5 border border-transparent rounded-md shadow-sm text-sm text-white focus:outline-none focus:ring-1 flex-shrink-0"
                  style={{ backgroundColor: '#0087D4', boxShadow: '0 1px 2px rgba(0, 135, 212, 0.3)', borderColor: '#0087D4' }}
                >
                  <svg className="-ml-0.5 mr-1 h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" clipRule="evenodd" />
                  </svg>
                  Create Notice
                </button>
              </div>
              
              {/* Row 2: Search Box */}
              <div className="relative w-full">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <svg className="h-4 w-4 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                    <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
                  </svg>
                </div>
                <input
                  type="text"
                  value={globalFilter ?? ''}
                  onChange={(e) => setGlobalFilter(e.target.value)}
                  className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  placeholder="Search notices..."
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Main Content */}
      <div className="container mx-auto px-4">

        {/* Table Section */}
        <div className="bg-white overflow-hidden rounded-lg shadow-sm border border-gray-200">
          <div className="overflow-x-auto">
            <table className="w-full divide-y divide-gray-200">
              <thead style={{ backgroundColor: 'rgba(0, 135, 212, 0.08)' }}>
                {table.getHeaderGroups().map(headerGroup => (
                  <tr key={headerGroup.id}>
                    {headerGroup.headers.map(header => (
                      <th
                        key={header.id}
                        scope="col"
                        className={`px-3 py-2 text-left text-xs font-semibold uppercase ${header.column.getCanSort() ? 'cursor-pointer select-none' : ''}`}
                        style={{ color: '#0087D4' }}
                        onClick={header.column.getToggleSortingHandler()}
                        title={header.column.getCanSort() ? (header.column.getNextSortingOrder() === 'asc' ? 'Sort ascending' : header.column.getNextSortingOrder() === 'desc' ? 'Sort descending' : 'Clear sort') : undefined}
                      >
                        {flexRender(header.column.columnDef.header, header.getContext())}
                        {{
                          asc: <span className="ml-1 text-blue-600">↑</span>,
                          desc: <span className="ml-1 text-blue-600">↓</span>,
                        }[header.column.getIsSorted()] ?? null}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody className="divide-y divide-gray-200">
                {loading && notices.length > 0 && (
                  <tr>
                    <td colSpan={columns.length} className="px-3 py-4 text-center">
                      <div className="flex items-center justify-center space-x-2">
                        <svg className="animate-spin h-4 w-4 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        <span className="text-sm">Loading...</span>
                      </div>
                    </td>
                  </tr>
                )}
                {!loading && table.getRowModel().rows.length === 0 && (
                  <tr>
                    <td colSpan={columns.length} className="px-3 py-6 text-center">
                      <p className="text-gray-600">
                        {globalFilter ? "No notices found matching your search." : "No notices found"}
                      </p>
                    </td>
                  </tr>
                )}
                {!loading && table.getRowModel().rows.map(row => (
                  <tr key={row.id} className="hover:bg-blue-50 transition-colors">
                    {row.getVisibleCells().map(cell => (
                      <td key={cell.id} className="px-3 py-2 text-sm text-gray-700">
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          {/* Enhanced Pagination */}
          {notices.length > 0 && (
            <div className="px-3 py-3 border-t border-gray-200 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs" style={{ backgroundColor: 'rgba(0, 135, 212, 0.02)' }}>
              <div className="flex items-center gap-4">
                <div className="text-gray-500">
                  Showing {table.getState().pagination.pageIndex * table.getState().pagination.pageSize + 1} to {Math.min((table.getState().pagination.pageIndex + 1) * table.getState().pagination.pageSize, table.getFilteredRowModel().rows.length)} of {table.getFilteredRowModel().rows.length} results
                </div>
                
                {/* Page Size Selector */}
                <div className="flex items-center gap-2">
                  <span className="text-gray-500">Show:</span>
                  <select
                    value={table.getState().pagination.pageSize}
                    onChange={e => {
                      table.setPageSize(Number(e.target.value));
                    }}
                    className="border border-gray-300 rounded px-2 py-1 text-xs bg-white focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  >
                    {[10, 20, 50, 100].map(pageSize => (
                      <option key={pageSize} value={pageSize}>
                        {pageSize}
                      </option>
                    ))}
                  </select>
                  <span className="text-gray-500">per page</span>
                </div>
              </div>
              
              <div className="flex items-center space-x-1">
                <button
                  onClick={() => table.setPageIndex(0)}
                  disabled={!table.getCanPreviousPage()}
                  className="px-3 py-1.5 border border-gray-300 bg-white text-gray-600 hover:bg-blue-50 hover:text-blue-700 disabled:opacity-40 rounded text-xs font-medium transition-colors"
                >
                  First
                </button>
                <button
                  onClick={() => table.previousPage()}
                  disabled={!table.getCanPreviousPage()}
                  className="px-3 py-1.5 border border-gray-300 bg-white text-gray-600 hover:bg-blue-50 hover:text-blue-700 disabled:opacity-40 rounded text-xs font-medium transition-colors"
                >
                  Previous
                </button>
                
                {/* Page Numbers */}
                <div className="flex items-center space-x-1">
                  {(() => {
                    const totalPages = table.getPageCount();
                    const currentPage = table.getState().pagination.pageIndex + 1;
                    const pageNumbers = [];
                    
                    // Show max 5 page numbers
                    let startPage = Math.max(1, currentPage - 2);
                    let endPage = Math.min(totalPages, startPage + 4);
                    
                    // Adjust start if we're near the end
                    if (endPage - startPage < 4) {
                      startPage = Math.max(1, endPage - 4);
                    }
                    
                    for (let i = startPage; i <= endPage; i++) {
                      pageNumbers.push(
                        <button
                          key={i}
                          onClick={() => table.setPageIndex(i - 1)}
                          className={`px-3 py-1.5 border rounded text-xs font-medium transition-colors ${
                            i === currentPage
                              ? 'text-white border-transparent'
                              : 'bg-white text-gray-600 border-gray-300 hover:bg-blue-50 hover:text-blue-700'
                          }`}
                          style={i === currentPage ? { backgroundColor: '#0087D4', borderColor: '#0087D4' } : {}}
                        >
                          {i}
                        </button>
                      );
                    }
                    
                    return pageNumbers;
                  })()}
                </div>
                
                <button
                  onClick={() => table.nextPage()}
                  disabled={!table.getCanNextPage()}
                  className="px-3 py-1.5 border border-gray-300 bg-white text-gray-600 hover:bg-blue-50 hover:text-blue-700 disabled:opacity-40 rounded text-xs font-medium transition-colors"
                >
                  Next
                </button>
                <button
                  onClick={() => table.setPageIndex(table.getPageCount() - 1)}
                  disabled={!table.getCanNextPage()}
                  className="px-3 py-1.5 border border-gray-300 bg-white text-gray-600 hover:bg-blue-50 hover:text-blue-700 disabled:opacity-40 rounded text-xs font-medium transition-colors"
                >
                  Last
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default SupplierNotices;
 